
import { gql, useQuery, useMutation, useApolloClient } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { Container, Header, Segment, List } from 'semantic-ui-react';

import { PATHS } from '../../paths';
import banner from '../../images/login-banner.png';
import styles from './Organisations.module.css';

const GET_ORGANISATIONS = gql`
    query Organisation {
        me {
            isAdminInWorkingOrganisation
            organisation {
                membership {
                    isPartnerLeadership
                    isPartnerMembershipOfficer
                }
            }
            organisations {
                id
                name
                isSupportingOrganisation
            }
        }
    }
`;

const SET_WORKING_ORGANISATION = gql`
    mutation SetWorkingOrganisation($organisationId: Int!) {
        setWorkingOrganisation(organisationId: $organisationId) {
            id
        }
    }
`;

const LOADING_MESSAGES = [
    'Loading your community…',
    'Connecting to your organisation…',
    'Almost ready…',
    'Just a moment more…',
];

const Organisations = () => {
    const navigate = useNavigate();
    const client = useApolloClient();
    const { loading, error, data } = useQuery(GET_ORGANISATIONS);
    const [setWorkingOrganisation] = useMutation(SET_WORKING_ORGANISATION);
    const [msgIndex, setMsgIndex] = useState(0);
    const [switching, setSwitching] = useState(false);
    const autoRouted = useRef(false);

    // Route the user to the right landing page for their role in their (now-set)
    // working organisation.
    const routeByRole = (me: any, chosenOrgId: number) => {
        if (me.isAdminInWorkingOrganisation) {
            navigate(PATHS.PEOPLE);
        } else if (me.organisation?.membership?.isPartnerLeadership
            || me.organisation?.membership?.isPartnerMembershipOfficer) {
            navigate(PATHS.LEADERSHIP);
        } else if (me.organisations.find((o: any) => o.id === chosenOrgId)?.isSupportingOrganisation) {
            navigate(PATHS.OVERVIEW);
        } else {
            navigate(PATHS.CLASSES);
        }
    };

    // Set the chosen org as the working org, then refetch every active query with the
    // new working org and WAIT for them before navigating. We use resetStore (not
    // clearStore): resetStore refetches active queries, whereas clearStore leaves them
    // holding their old (org-less) result — which makes the Layout auth guard bounce us
    // back to /organisations right after we navigate, causing a flicker.
    const chooseOrganisation = async (organisationId: number) => {
        if (switching) return;
        setSwitching(true);

        try {
            await setWorkingOrganisation({ variables: { organisationId } });
        } catch (e) {
            // Couldn't set the working org — stay on the picker so they can retry.
            setSwitching(false);
            return;
        }

        // Working org is set. Refetch active queries with the new org and wait, so the
        // Layout auth guard's `me` no longer holds stale org-less data (which would
        // bounce us back to /organisations). Even if a refetch hiccups, still navigate.
        try {
            await client.resetStore();
        } catch { /* ignore refetch errors; destination loads its own data */ }

        try {
            const { data: refreshed } = await client.query({
                query: GET_ORGANISATIONS,
                fetchPolicy: 'cache-first',
            });
            routeByRole(refreshed.me, organisationId);
        } catch {
            navigate(PATHS.CLASSES);
        }
    };

    useEffect(() => {
        if (!data) return;

        const orgs = data.me.organisations;
        const hasWorkingOrg = data.me.organisation != null;

        // No memberships at all — nothing to pick, so log out.
        if (orgs.length === 0) {
            navigate(PATHS.LOGOUT);
            return;
        }

        // Already scoped to a single org: go straight in (existing landing behaviour).
        // Everyone else — no working org set (any number of orgs), or multiple orgs —
        // is shown the picker to choose from.
        //
        // Same resetStore rationale as chooseOrganisation below: without it, Layout's
        // GET_ME guard can still be holding an org-less `me` (e.g. right after login)
        // and bounce us straight back to /organisations after this navigate. Guarded by
        // autoRouted since resetStore refetches this component's own active query,
        // which would otherwise re-run this effect and reset the store forever.
        if (hasWorkingOrg && orgs.length === 1) {
            if (autoRouted.current) return;
            autoRouted.current = true;
            client.resetStore().finally(() => routeByRole(data.me, orgs[0].id));
        }
    }, [data]);

    // Redirect to logout if still loading after 5 seconds (but not while switching org).
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (loading && !switching) navigate(PATHS.LOGOUT);
        }, 5000);
        return () => clearTimeout(timeout);
    }, [loading, switching]);

    // Cycle through messages while loading
    useEffect(() => {
        if (!loading) return;
        const interval = setInterval(() => {
            setMsgIndex(i => Math.min(i + 1, LOADING_MESSAGES.length - 1));
        }, 1200);
        return () => clearInterval(interval);
    }, [loading]);

    // Show the picker when the user needs to choose: no working org is set (any number
    // of orgs), or they belong to more than one. Single-org users who already have a
    // working org are auto-routed by the effect above, so they keep seeing the
    // "Loading your community…" screen until it navigates — the picker never flashes.
    const orgCount = data?.me?.organisations?.length ?? 0;
    const hasWorkingOrg = data?.me?.organisation != null;
    const shouldShowPicker = orgCount > 0 && (!hasWorkingOrg || orgCount > 1);

    if (loading || error || switching || !shouldShowPicker) {
        return (
            <div className={styles.page}>
                <div className={styles.brandBar}>
                    <img src={banner} alt="Joyya" className={styles.brandLogo} />
                </div>
                <div className={styles.content}>
                    <div className={styles.spinner} />
                    <p key={msgIndex} className={styles.message}>
                        {error ? 'Something went wrong. Please try again.' : LOADING_MESSAGES[msgIndex]}
                    </p>
                    {!error && (
                        <div className={styles.progressTrack}>
                            <div className={styles.progressFill} />
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <Segment style={{ padding: '2em 0em' }} vertical>
            <Container text>
                <Header as='h1'>Choose your organisation</Header>
                <p>Select the organisation you'd like to work in.</p>
                <List divided relaxed selection size='large'>
                    {data.me.organisations.map((o: any) => (
                        <List.Item
                            key={o.id}
                            onClick={() => chooseOrganisation(o.id)}
                            style={{ cursor: 'pointer', padding: '1em' }}
                        >
                            <List.Icon name='building' size='large' verticalAlign='middle' />
                            <List.Content>
                                <List.Header>{o.name}</List.Header>
                            </List.Content>
                        </List.Item>
                    ))}
                </List>
            </Container>
        </Segment>
    );
};

export { Organisations };
